import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../components/common/BackButton';
import toast from 'react-hot-toast';
import {
  getJob,
  createJob,
  updateJob,
  checkStock,
} from '../../../services/jobsService';
import { getDesigners } from '../../../services/usersService';
import { getServices } from '../../../services/servicesService';
import { getApiErrorMessage } from '../../../services/http';

const emptyLineItem = { serviceType: '', description: '', quantity: 1, unitPrice: 0 };
// Fallback sample services shown if no services are configured yet
const defaultServiceSamples = [
  { _id: 'sample-1', name: 'Business Card', defaultPrice: 50 },
  { _id: 'sample-2', name: 'Flyer A5', defaultPrice: 80 },
  { _id: 'sample-3', name: 'Banner 3x1m', defaultPrice: 300 },
  { _id: 'sample-4', name: 'Sticker Sheet', defaultPrice: 40 },
  { _id: 'sample-5', name: 'T-Shirt Print', defaultPrice: 120 },
];

const JobForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Customer fields
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  // Job fields
  const [expectedDueDate, setExpectedDueDate] = useState('');
  const [advancePayment, setAdvancePayment] = useState(0);
  const [advancePaymentMethod, setAdvancePaymentMethod] = useState('cash');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountCollected, setAmountCollected] = useState(0);
  const [advanceReceivedAt, setAdvanceReceivedAt] = useState('');
  const [assignedDesigner, setAssignedDesigner] = useState('');
  const [notes, setNotes] = useState('');

  // Line items
  const [lineItems, setLineItems] = useState([ { ...emptyLineItem } ]);

  // Attachments
  const [attachments, setAttachments] = useState([]);

  // Options
  const [services, setServices] = useState([]);
  const [designers, setDesigners] = useState([]);

  // Loading state
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingJob, setLoadingJob] = useState(!!id);
  
  // Stock checking
  const [stockCheck, setStockCheck] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [servicesData, designersData] = await Promise.all([
          getServices({ limit: 1000 }),
          getDesigners()
        ]);
        setServices(servicesData?.services || servicesData || []);
        setDesigners(designersData || []);
      } catch (error) {
        console.error('Failed to load options', error);
        toast.error(getApiErrorMessage(error, 'Failed to load form options'));
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, []);

  // Fetch existing job when editing
  useEffect(() => {
    const loadJob = async () => {
      if (!id) return;
      try {
        setLoadingJob(true);
        const j = await getJob(id);
        if (!j) return;
        setCustomerName(j.customer?.name || '');
        setCustomerPhone(j.customer?.phone || '');
        setCustomerEmail(j.customer?.email || '');
        const toDateInput = (d) => {
          if (!d) return '';
          const dt = new Date(d);
          const y = dt.getFullYear();
          const m = String(dt.getMonth() + 1).padStart(2, '0');
          const day = String(dt.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        setExpectedDueDate(toDateInput(j.expectedDueDate));
        setAdvancePayment(j.advancePayment || 0);
        setAdvanceReceivedAt(toDateInput(j.advanceReceivedAt));
        setAssignedDesigner(j.assignedDesigner?._id || (typeof j.assignedDesigner === 'string' ? j.assignedDesigner : ''));
        setNotes(j.notes || '');
        setLineItems(Array.isArray(j.lineItems) && j.lineItems.length > 0
          ? j.lineItems.map(li => ({
              serviceType: li.serviceType || '',
              description: li.description || '',
              quantity: Number(li.quantity) || 1,
              unitPrice: Number(li.unitPrice) || 0
            }))
          : [{ ...emptyLineItem }]
        );
        setPaymentMethod(j.paymentMethod || 'cash');
        setAmountCollected(j.amountCollected || 0);
      } catch (e) {
        console.error('Failed to load job', e);
        toast.error(getApiErrorMessage(e, 'Failed to load job'));
      } finally {
        setLoadingJob(false);
      }
    };
    loadJob();
  }, [id]);

  const serviceOptions = services && services.length > 0 ? services : defaultServiceSamples;

  const handleAddLineItem = () => {
    setLineItems(prev => [ ...prev, { ...emptyLineItem } ]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleServiceChange = (index, value) => {
    // Update service type
    let next = [...lineItems];
    next[index] = { ...next[index], serviceType: value };
    // If there is a matching service with defaultPrice, prefill unitPrice when empty or zero
    const svc = serviceOptions.find(s => s.name === value);
    if (svc && (Number(next[index].unitPrice) === 0 || next[index].unitPrice === '' || next[index].unitPrice === undefined)) {
      next[index].unitPrice = Number(svc.defaultPrice || 0);
    }
    setLineItems(next);
  };

  const totalPrice = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);

  const balanceDue = useMemo(() => {
    const adv = Number(advancePayment) || 0;
    return Math.max(totalPrice - adv, 0);
  }, [totalPrice, advancePayment]);

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(files);
  };

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!customerPhone.trim()) {
      toast.error('Customer phone is required');
      return false;
    }
    if (!expectedDueDate) {
      toast.error('Expected due date is required');
      return false;
    }
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
      toast.error('Add at least one line item');
      return false;
    }
    for (const item of lineItems) {
      if (!item.serviceType?.trim()) {
        toast.error('Each line item needs a service');
        return false;
      }
      const qty = Number(item.quantity);
      const price = Number(item.unitPrice);
      if (!(qty > 0)) {
        toast.error('Quantity must be greater than 0');
        return false;
      }
      if (!(price >= 0)) {
        toast.error('Unit price must be 0 or greater');
        return false;
      }
    }
    return true;
  };

  const checkStockAvailability = async () => {
    if (lineItems.length === 0 || !lineItems[0].serviceType) {
      toast.error('Please add at least one line item with a service type');
      return;
    }
    
    try {
      setCheckingStock(true);
      const data = await checkStock(
        lineItems.filter(item => item.serviceType && item.quantity > 0)
      );
      
      setStockCheck(data);
      
      if (data.canProceed) {
        toast.success('Stock check passed! You can proceed with job creation.');
      } else {
        toast.error('Insufficient stock for some items. Please check stock levels.');
      }
    } catch (error) {
      console.error('Stock check error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to check stock availability'));
    } finally {
      setCheckingStock(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    // Check stock before submitting if creating a new job
    if (!id && !stockCheck) {
      toast.error('Please check stock availability before creating the job');
      return;
    }
    
    if (!id && !stockCheck.canProceed) {
      toast.error('Cannot create job due to insufficient stock');
      return;
    }

    try {
      setSubmitting(true);
      if (id) {
        const payload = {
          customer: {
            name: customerName.trim(),
            phone: customerPhone.trim(),
            ...(customerEmail.trim() ? { email: customerEmail.trim() } : {})
          },
          lineItems: lineItems.map(it => ({
            serviceType: it.serviceType,
            description: it.description,
            quantity: Number(it.quantity) || 0,
            unitPrice: Number(it.unitPrice) || 0
          })),
          expectedDueDate,
          advancePayment: Number(advancePayment || 0),
          ...(assignedDesigner ? { assignedDesigner } : {}),
          notes
        };
        await updateJob(id, payload);
        toast.success('Job updated successfully');
        navigate(`/jobs/${id}`);
      } else {
        const form = new FormData();
        form.append('customer[name]', customerName.trim());
        form.append('customer[phone]', customerPhone.trim());
        if (customerEmail.trim()) form.append('customer[email]', customerEmail.trim());
        lineItems.forEach((item, index) => {
          form.append(`lineItems[${index}][serviceType]`, item.serviceType);
          if (item.description) form.append(`lineItems[${index}][description]`, item.description);
          form.append(`lineItems[${index}][quantity]`, String(item.quantity));
          form.append(`lineItems[${index}][unitPrice]`, String(item.unitPrice));
        });
        form.append('expectedDueDate', expectedDueDate);
        form.append('advancePayment', String(advancePayment || 0));
        form.append('advancePaymentMethod', advancePaymentMethod);
        if (advanceReceivedAt) form.append('advanceReceivedAt', advanceReceivedAt);
        if (assignedDesigner) form.append('assignedDesigner', assignedDesigner);
        if (notes) form.append('notes', notes);
        attachments.forEach(file => {
          form.append('attachments', file);
        });
        const response = await createJob(form);
        toast.success('Job created successfully');
        const newJobId = response?._id;
        if (newJobId) {
          navigate(`/jobs/${newJobId}`);
        } else {
          navigate('/jobs');
        }
      }
    } catch (error) {
      console.error('Create job failed', error);
      toast.error(getApiErrorMessage(error, 'Failed to create job'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingOptions || loadingJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-3">
        <BackButton to="/jobs" />
        <h2 className="text-2xl font-bold text-gray-900">{id ? 'Edit Job' : 'New Job'}</h2>
      </div>

      {/* Customer Info */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-blue-200 bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50">
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-blue-900">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input className="input-field" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input className="input-field" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="e.g. 07XXXXXXXX" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
            <input className="input-field" type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="email@example.com" />
          </div>
        </div>
        </div>
      </div>

      {/* Job Details */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-green-900">Job Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Due Date</label>
            <input className="input-field" type="date" value={expectedDueDate} onChange={e => setExpectedDueDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Advance Payment</label>
            <input className="input-field" type="number" step="0.01" value={advancePayment} onChange={e => setAdvancePayment(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Advance Method</label>
            <select className="input-field" value={advancePaymentMethod} onChange={e => setAdvancePaymentMethod(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="till">Till</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Advance Received Date</label>
            <input className="input-field" type="date" value={advanceReceivedAt} onChange={e => setAdvanceReceivedAt(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign Designer (optional)</label>
            <select className="input-field" value={assignedDesigner} onChange={e => setAssignedDesigner(e.target.value)}>
              <option value="">— Select —</option>
              {designers.map(d => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea className="input-field" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes"></textarea>
        </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-purple-900">Line Items</h3>
          <button type="button" onClick={handleAddLineItem} className="btn-primary">Add Item</button>
        </div>

        <div className="space-y-4">
          {lineItems.map((item, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                <select
                  className="input-field"
                  value={item.serviceType}
                  onChange={e => handleServiceChange(index, e.target.value)}
                >
                  <option value="">— Select service —</option>
                  {serviceOptions.map(s => (
                    <option key={s._id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  className="input-field"
                  value={item.description}
                  onChange={e => handleLineItemChange(index, 'description', e.target.value)}
                  placeholder="Details (size, color, material, etc.)"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="input-field"
                  value={item.quantity}
                  onChange={e => handleLineItemChange(index, 'quantity', Number(e.target.value))}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  value={item.unitPrice}
                  onChange={e => handleLineItemChange(index, 'unitPrice', Number(e.target.value))}
                />
              </div>

              <div className="md:col-span-1 text-right">
                <div className="text-sm text-gray-500 mb-1">Total</div>
                <div className="text-base font-semibold text-gray-900">KSh{(Number(item.quantity||0) * Number(item.unitPrice||0)).toFixed(2)}</div>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-red-600 mt-1"
                    onClick={() => handleRemoveLineItem(index)}
                  >Remove</button>
                )}
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>

      {!id && (
        <div className="relative overflow-hidden rounded-2xl shadow-lg border border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-bold text-amber-900">Attachments</h3>
            <input type="file" multiple onChange={onFileChange} className="block w-full text-sm text-gray-600" />
            <p className="text-xs text-gray-500">Allowed: images, PDFs, and common documents. Max 10MB each.</p>
          </div>
        </div>
      )}

      {/* Summary & Actions */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg border border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50">
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-indigo-900">Summary & Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="text-sm text-gray-600">Total Price</div>
            <div className="text-2xl font-semibold text-gray-900">KSh{totalPrice.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-sm text-gray-600">Balance Due</div>
            <div className="text-2xl font-semibold text-gray-900">KSh{balanceDue.toFixed(2)}</div>
          </div>
        </div>
        {/* Stock Check Section - Only show for new jobs */}
        {!id && (
          <div className="mt-6 p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">Stock Availability Check</h4>
              <button
                type="button"
                onClick={checkStockAvailability}
                disabled={checkingStock || lineItems.length === 0}
                className="btn-secondary"
              >
                {checkingStock ? 'Checking...' : 'Check Stock'}
              </button>
            </div>
            
            {stockCheck && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-gray-900">{stockCheck.summary.total}</div>
                    <div className="text-gray-600">Total Items</div>
                  </div>
                  <div className="text-center">
                    <div className={`font-semibold ${stockCheck.summary.available > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {stockCheck.summary.available}
                    </div>
                    <div className="text-gray-600">Available</div>
                  </div>
                  <div className="text-center">
                    <div className={`font-semibold ${stockCheck.summary.insufficient > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {stockCheck.summary.insufficient}
                    </div>
                    <div className="text-gray-600">Insufficient</div>
                  </div>
                  <div className="text-center">
                    <div className={`font-semibold ${stockCheck.summary.lowStock > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                      {stockCheck.summary.lowStock}
                    </div>
                    <div className="text-gray-600">Low Stock</div>
                  </div>
                </div>
                
                {stockCheck.insufficientStock.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="text-red-800 font-medium mb-2">⚠️ Insufficient Stock:</div>
                    <ul className="text-red-700 text-sm space-y-1">
                      {stockCheck.insufficientStock.map((item, index) => (
                        <li key={index}>
                          {item.serviceType}: Required {item.required} {item.unit}, Available {item.available} {item.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {stockCheck.lowStockItems.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-3">
                    <div className="text-orange-800 font-medium mb-2">⚠️ Low Stock Items:</div>
                    <ul className="text-orange-700 text-sm space-y-1">
                      {stockCheck.lowStockItems.map((item, index) => (
                        <li key={index}>
                          {item.stockName}: {item.available} {item.unit} remaining
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {stockCheck.canProceed && (
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="text-green-800 font-medium">✅ Stock check passed! You can proceed with job creation.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

          <div className="mt-6 flex items-center gap-3">
            <button type="submit" disabled={submitting || (!id && (!stockCheck || !stockCheck.canProceed))} className="btn-primary">
              {submitting ? 'Saving...' : (id ? 'Save Changes' : 'Create Job')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/jobs')}>Cancel</button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default JobForm; 